import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO = process.env.PUBLIC_URL + "/apple-touch-icon.png";
const DURATION_MS = 2600;
const EXIT_MS = 550;

export default function SplashScreen({ onDone }) {
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    const shown = sessionStorage.getItem("splash_shown");
    if (shown) { onDone(); return; }

    const exitTimer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => {
        setMounted(false);
        sessionStorage.setItem("splash_shown", "1");
        onDone();
      }, EXIT_MS);
    }, DURATION_MS);

    return () => clearTimeout(exitTimer);
  }, []); // eslint-disable-line

  if (!mounted) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="splash"
        initial={{ opacity: 1 }}
        animate={{ opacity: exiting ? 0 : 1 }}
        transition={{ duration: EXIT_MS / 1000, ease: "easeInOut" }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "#000000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* ── Ambient golden glow behind logo ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: exiting ? 0 : 0.28, scale: 1.6 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          style={{
            position: "absolute",
            width: "clamp(180px, 50vw, 280px)",
            height: "clamp(180px, 50vw, 280px)",
            borderRadius: "50%",
            background: "radial-gradient(circle at center, #FEC400 0%, rgba(254,196,0,0.3) 40%, transparent 70%)",
            filter: "blur(28px)",
            pointerEvents: "none",
          }}
        />

        {/* ── Center content ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.82, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22, delay: 0.06 }}
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
          }}
        >
          {/* ── Logo with spinning ring ── */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {/* Outer spinning arc */}
            <motion.svg
              width="clamp(130px, 32vw, 156px)"
              height="clamp(130px, 32vw, 156px)"
              viewBox="0 0 156 156"
              style={{ position: "absolute" }}
              animate={{ rotate: 360 }}
              transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
            >
              <circle
                cx="78" cy="78" r="72"
                fill="none"
                stroke="url(#arcGrad)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray="90 360"
              />
              <defs>
                <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FEC400" stopOpacity="0" />
                  <stop offset="50%" stopColor="#FEC400" stopOpacity="1" />
                  <stop offset="100%" stopColor="#FEC400" stopOpacity="0" />
                </linearGradient>
              </defs>
            </motion.svg>

            {/* Static outer ring */}
            <div style={{
              position: "absolute",
              width: "clamp(116px, 28vw, 140px)",
              height: "clamp(116px, 28vw, 140px)",
              borderRadius: "50%",
              border: "1px solid rgba(254,196,0,0.12)",
            }} />

            {/* Logo image */}
            <img
              src={LOGO}
              alt="Qkiere"
              style={{
                width: "clamp(80px, 20vw, 100px)",
                height: "clamp(80px, 20vw, 100px)",
                borderRadius: "clamp(18px, 4.5vw, 22px)",
                objectFit: "contain",
                background: "#0d0d0d",
                display: "block",
                position: "relative",
                boxShadow:
                  "0 0 0 1px rgba(254,196,0,0.25), " +
                  "0 0 32px rgba(254,196,0,0.18), " +
                  "0 8px 32px rgba(0,0,0,0.9)",
              }}
              onError={(e) => {
                // Fallback to jpg if png not found
                if (!e.target.src.endsWith(".jpg")) {
                  e.target.src = process.env.PUBLIC_URL + "/apple-touch-icon.jpg";
                }
              }}
            />
          </div>

          {/* ── Wordmark ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42, duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: "flex", alignItems: "baseline", gap: 1 }}
          >
            <span style={{
              fontSize: "clamp(30px, 7.5vw, 38px)",
              fontWeight: 900,
              color: "#FEC400",
              lineHeight: 1,
              fontFamily: "'Poppins', 'Segoe UI', system-ui, sans-serif",
              letterSpacing: "-0.5px",
            }}>Q</span>
            <span style={{
              fontSize: "clamp(30px, 7.5vw, 38px)",
              fontWeight: 900,
              color: "#ffffff",
              lineHeight: 1,
              fontFamily: "'Poppins', 'Segoe UI', system-ui, sans-serif",
              letterSpacing: "-0.5px",
            }}>kiere</span>
            <span style={{
              fontSize: "clamp(8px, 2vw, 10px)",
              color: "rgba(254,196,0,0.55)",
              marginLeft: 3,
              marginBottom: 2,
              fontFamily: "system-ui, sans-serif",
              alignSelf: "flex-end",
            }}>®</span>
          </motion.div>

          {/* ── Tagline ── */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 0.85, duration: 0.5 }}
            style={{
              margin: "-8px 0 0",
              fontSize: "clamp(9px, 2.2vw, 11px)",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontFamily: "'Segoe UI', system-ui, sans-serif",
              fontWeight: 500,
            }}
          >
            Pide lo que quieras
          </motion.p>
        </motion.div>

        {/* ── Bottom progress bar ── */}
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          height: 2,
          background: "rgba(254,196,0,0.08)",
        }}>
          <motion.div
            initial={{ width: "0%", opacity: 0 }}
            animate={{ width: "100%", opacity: 1 }}
            transition={{
              width: { duration: DURATION_MS / 1000, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2, delay: 0.2 },
            }}
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #FEC400, #ffe566)",
              borderRadius: "0 2px 2px 0",
              transformOrigin: "left",
            }}
          />
        </div>

        {/* ── Safe area bottom padding (iOS notch) ── */}
        <div style={{ position: "absolute", bottom: 0, height: "env(safe-area-inset-bottom)" }} />
      </motion.div>
    </AnimatePresence>
  );
}
